import React, { useMemo, useState } from 'react';
import InputLabel from '../../components/InputLabel';
import TextInput from '../../components/TextInput';
import cbbFavicon from '../../images/cbb-favicon.png';
import { 
    CheckCircleIcon, 
    ExclamationTriangleIcon, 
    ArrowPathIcon,
    GlobeAltIcon,
    EyeIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon
} from '@heroicons/react/24/outline';

// Helper to convert Croatian text to clean URL slug
function generateSlug(text) {
    if (!text) return '';
    return text
        .toLowerCase()
        .trim()
        .replace(/đ/g, 'd')
        .replace(/č|ć/g, 'c')
        .replace(/š/g, 's')
        .replace(/ž/g, 'z')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

// Helper to calculate Yoast-style score for SEO Title (optimal: 35-60 chars)
export function getTitleScore(title) {
    const len = (title || '').trim().length;
    if (len === 0) {
        return { color: 'bg-rose-500', textColor: 'text-rose-600', percent: 0, status: 'error', message: 'SEO naslov je prazan' };
    }
    if (len < 35) {
        const pct = Math.min(100, Math.round((len / 60) * 100));
        return { color: 'bg-amber-500', textColor: 'text-amber-600', percent: pct, status: 'warning', message: `Prekratak naslov (${len}/60). Preporučeno: 35–60 znakova.` };
    }
    if (len <= 60) {
        const pct = Math.min(100, Math.round((len / 60) * 100));
        return { color: 'bg-emerald-500', textColor: 'text-emerald-600', percent: pct, status: 'success', message: `Optimalna duljina za Google prikaz (${len}/60).` };
    }
    return { color: 'bg-rose-500', textColor: 'text-rose-600', percent: 100, status: 'error', message: `Predugačak naslov (${len} znakova). Google će odrezati tekst s "..."` };
}

// Helper to calculate Yoast-style score for Meta Description (optimal: 120-156 chars)
export function getDescScore(desc) {
    const len = (desc || '').trim().length;
    if (len === 0) {
        return { color: 'bg-rose-500', textColor: 'text-rose-600', percent: 0, status: 'error', message: 'Meta opis je prazan' };
    }
    if (len < 120) {
        const pct = Math.min(100, Math.round((len / 156) * 100));
        return { color: 'bg-amber-500', textColor: 'text-amber-600', percent: pct, status: 'warning', message: `Prekratak opis (${len}/156). Preporučeno: 120–156 znakova.` };
    }
    if (len <= 156) {
        const pct = Math.min(100, Math.round((len / 156) * 100));
        return { color: 'bg-emerald-500', textColor: 'text-emerald-600', percent: pct, status: 'success', message: `Optimalna duljina (${len}/156 znakova). Izvrsno za pretraživače!` };
    }
    return { color: 'bg-rose-500', textColor: 'text-rose-600', percent: 100, status: 'error', message: `Predugačak opis (${len} znakova). Google će odrezati višak.` };
}

export default function SeoSection({
    formData,
    business,
    featuredImageUrl,
    categories = [],
    customSeoEnabled,
    onToggleCustomSeo,
    seoData,
    onChangeSeoData,
    onResetToFormula,
    computedAutoTitle,
    computedAutoDesc,
}) {
    // Toggle for Mobile vs Desktop Google preview
    const [previewMode, setPreviewMode] = useState('mobile'); // 'mobile' | 'desktop'

    // Current active title & description (either custom or automatic formula)
    const activeTitle = customSeoEnabled && seoData.title.trim() ? seoData.title : computedAutoTitle;
    const activeDesc = customSeoEnabled && seoData.description.trim() ? seoData.description : computedAutoDesc;

    // Yoast Scores
    const titleScore = useMemo(() => getTitleScore(activeTitle), [activeTitle]);
    const descScore = useMemo(() => getDescScore(activeDesc), [activeDesc]);

    // Keyphrase checks
    const kw = (seoData.focuskw || '').trim().toLowerCase();
    const kwInTitle = kw ? activeTitle.toLowerCase().includes(kw) : null;
    const kwInDesc = kw ? activeDesc.toLowerCase().includes(kw) : null;

    // Overall traffic light indicator (Green / Orange / Red)
    const overallTrafficLight = useMemo(() => {
        if (!customSeoEnabled) return 'bg-emerald-500'; // Automatic formula is calibrated good
        if (titleScore.status === 'success' && descScore.status === 'success') return 'bg-emerald-500';
        if (titleScore.status === 'error' || descScore.status === 'error') return 'bg-rose-500';
        return 'bg-amber-500';
    }, [customSeoEnabled, titleScore, descScore]);

    // Live URL slug (matches WordPress slug format)
    const listingSlug = business?.slug || generateSlug(formData.naziv_brenda) || 'poslovni-oglas';

    return (
        <div className="border border-gray-200 shadow-md rounded-xl p-5 bg-white space-y-6">
            {/* Header bar with Yoast badge and Mode switch */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                    <span className="p-2 bg-indigo-50 text-indigo-700 rounded-lg">
                        <GlobeAltIcon className="w-6 h-6" />
                    </span>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-base">Optimizacija za tražilice (Yoast SEO)</h3>
                            <span className={`inline-block w-3 h-3 rounded-full ${overallTrafficLight} ring-4 ring-gray-100`} title="SEO Semafor Ocjena"></span>
                        </div>
                        <p className="text-xs text-gray-500">
                            {customSeoEnabled 
                                ? 'Ručni unos aktivan — unesene vrijednosti prepisuju zadanu formulu' 
                                : 'Automatski način — naslov i opis se generiraju dinamički iz podataka oglasa'}
                        </p>
                    </div>
                </div>

                {/* Toggle switch for Custom Mode */}
                <label className="inline-flex items-center cursor-pointer select-none bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                    <input
                        type="checkbox"
                        checked={customSeoEnabled}
                        onChange={onToggleCustomSeo}
                        className="sr-only peer"
                    />
                    <div className="relative w-10 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-900"></div>
                    <span className="ms-3 text-xs font-semibold text-gray-700">Prilagodi SEO ručno</span>
                </label>
            </div>

            {/* Google Search Live Preview Section */}
            <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <EyeIcon className="w-4 h-4 text-gray-400" />
                        Prikaz na Google pretraživaču (Snippet Preview)
                    </span>

                    {/* Mobile / Desktop Preview Toggle Buttons */}
                    <div className="inline-flex items-center p-1 bg-gray-100 rounded-lg border border-gray-200 text-xs">
                        <button
                            type="button"
                            onClick={() => setPreviewMode('mobile')}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md transition-all cursor-pointer ${
                                previewMode === 'mobile'
                                    ? 'bg-white text-blue-950 font-semibold shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <DevicePhoneMobileIcon className="w-4 h-4" />
                            Mobilni prikaz
                        </button>
                        <button
                            type="button"
                            onClick={() => setPreviewMode('desktop')}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md transition-all cursor-pointer ${
                                previewMode === 'desktop'
                                    ? 'bg-white text-blue-950 font-semibold shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <ComputerDesktopIcon className="w-4 h-4" />
                            Računalo prikaz
                        </button>
                    </div>
                </div>

                {/* Preview Box Container */}
                <div className="p-4 bg-gray-50/60 rounded-xl border border-gray-200">
                    {previewMode === 'mobile' ? (
                        /* MOBILE GOOGLE RESULT CARD WITH FEATURED THUMBNAIL */
                        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm max-w-md font-sans">
                            {/* Header row with SQUARE CBB Favicon + Site title + URL */}
                            <div className="flex items-center gap-2 mb-2">
                                <img
                                    src={cbbFavicon}
                                    alt="CBB"
                                    className="w-5 h-5 rounded-none object-contain flex-shrink-0 shadow-xs"
                                    onError={(e) => {
                                        e.target.src = `${process.env.PUBLIC_URL}/cbb-favicon.png`;
                                    }}
                                />
                                <div className="flex flex-col min-w-0 leading-tight">
                                    <span className="text-xs font-semibold text-[#202124]">CBB</span>
                                    <span className="text-[11px] text-[#4d5156] truncate">
                                        https://cbb.hr › trenutne-ponude › {listingSlug}
                                    </span>
                                </div>
                            </div>

                            {/* Mobile Content Row: Title & Description on left, Featured Image on right */}
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    {/* Blue Title */}
                                    <h4 className="text-base text-[#1a0dab] font-medium leading-snug line-clamp-2 hover:underline cursor-pointer">
                                        {activeTitle || 'Naziv Brenda | Prodaja Biznisa - CBB.hr'}
                                    </h4>
                                    {/* Description */}
                                    <p className="text-xs text-[#4d5156] mt-1.5 leading-relaxed line-clamp-3">
                                        {activeDesc || 'Kratki sažetak poslovanja s informacijama o cijeni, lokaciji i uvjetima preuzimanja.'}
                                    </p>
                                </div>

                                {/* Yoast-style Mobile Featured Image Thumbnail */}
                                {featuredImageUrl && (
                                    <div className="flex-shrink-0">
                                        <img
                                            src={featuredImageUrl}
                                            alt="Featured Preview"
                                            className="w-20 h-20 rounded-xl object-cover border border-gray-200 shadow-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* DESKTOP GOOGLE RESULT CARD */
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm max-w-2xl font-sans">
                            {/* Header row with SQUARE CBB Favicon + CBB site name on top + URL below */}
                            <div className="flex items-center gap-2.5 mb-2">
                                <img
                                    src={cbbFavicon}
                                    alt="CBB"
                                    className="w-6 h-6 rounded-none object-contain flex-shrink-0 shadow-xs"
                                    onError={(e) => {
                                        e.target.src = `${process.env.PUBLIC_URL}/cbb-favicon.png`;
                                    }}
                                />
                                <div className="flex flex-col min-w-0 leading-tight">
                                    <span className="text-sm font-semibold text-[#202124]">CBB</span>
                                    <span className="text-xs text-[#4d5156] truncate">
                                        https://cbb.hr › trenutne-ponude › {listingSlug}
                                    </span>
                                </div>
                            </div>
                            {/* Blue Title */}
                            <h4 className="text-lg text-[#1a0dab] font-medium leading-normal hover:underline cursor-pointer">
                                {activeTitle || 'Naziv Brenda | Prodaja Biznisa - CBB.hr'}
                            </h4>
                            {/* Description */}
                            <p className="text-sm text-[#4d5156] mt-1 leading-normal line-clamp-2">
                                {activeDesc || 'Kratki sažetak poslovanja s informacijama o cijeni, lokaciji i uvjetima preuzimanja.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Editable Fields (Shown when Custom Mode is enabled) */}
            {customSeoEnabled && (
                <div className="pt-2 border-t border-gray-100 space-y-5 animate-fadeIn">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-blue-950 uppercase tracking-wider">
                            Polja za prilagodbu
                        </span>
                        <button
                            type="button"
                            onClick={onResetToFormula}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-900 hover:text-blue-700 hover:underline cursor-pointer"
                        >
                            <ArrowPathIcon className="w-3.5 h-3.5" />
                            Vrati na zadanu formulu
                        </button>
                    </div>

                    {/* SEO Title Input (with shadow-md matching main form) */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <InputLabel value="SEO Naslov (Title tag)" />
                            <span className={`text-xs font-medium ${titleScore.textColor}`}>
                                {titleScore.message}
                            </span>
                        </div>
                        <TextInput
                            type="text"
                            value={seoData.title}
                            onChange={(e) => onChangeSeoData('title', e.target.value)}
                            placeholder="Unesite prilagođeni SEO naslov..."
                            className="w-full text-sm shadow-md"
                        />
                        {/* Yoast Progress Bar */}
                        <div className="w-full bg-gray-200 h-1.5 rounded-full mt-1.5 overflow-hidden">
                            <div 
                                className={`h-full transition-all duration-300 ${titleScore.color}`} 
                                style={{ width: `${titleScore.percent}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Meta Description Input (with shadow-md matching main form) */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <InputLabel value="Meta Opis (Meta Description)" />
                            <span className={`text-xs font-medium ${descScore.textColor}`}>
                                {descScore.message}
                            </span>
                        </div>
                        <textarea
                            rows={3}
                            value={seoData.description}
                            onChange={(e) => onChangeSeoData('description', e.target.value)}
                            placeholder="Unesite prilagođeni meta opis..."
                            className="p-3 border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-md w-full resize-none text-sm"
                        />
                        {/* Yoast Progress Bar */}
                        <div className="w-full bg-gray-200 h-1.5 rounded-full mt-1.5 overflow-hidden">
                            <div 
                                className={`h-full transition-all duration-300 ${descScore.color}`} 
                                style={{ width: `${descScore.percent}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Focus Keyphrase & Rules (with shadow-md matching main form) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div>
                            <InputLabel value="Fokusna ključna riječ (Focus Keyphrase)" />
                            <TextInput
                                type="text"
                                value={seoData.focuskw}
                                onChange={(e) => onChangeSeoData('focuskw', e.target.value)}
                                placeholder="npr. prodaja restorana zagreb"
                                className="w-full text-sm mt-1 shadow-md"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">Glavni pojam za koji želite rangirati na tražilicama.</p>
                        </div>

                        {/* Live Yoast Assessments */}
                        {kw ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-2">
                                <span className="font-semibold text-gray-700 block text-[11px] uppercase tracking-wider">
                                    Provjera ključne riječi:
                                </span>
                                <div className="flex items-center gap-2">
                                    {kwInTitle ? (
                                        <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    ) : (
                                        <ExclamationTriangleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                    )}
                                    <span className={kwInTitle ? 'text-emerald-700 font-medium' : 'text-rose-600'}>
                                        {kwInTitle ? 'Ključna riječ se nalazi u SEO naslovu' : 'Ključna riječ nedostaje u SEO naslovu'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {kwInDesc ? (
                                        <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    ) : (
                                        <ExclamationTriangleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                    )}
                                    <span className={kwInDesc ? 'text-emerald-700 font-medium' : 'text-rose-600'}>
                                        {kwInDesc ? 'Ključna riječ se nalazi u meta opisu' : 'Ključna riječ nedostaje u meta opisu'}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-xs text-gray-400">
                                Unesite fokusnu ključnu riječ za Yoast provjeru prisutnosti u naslovu i opisu.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
